<template>
    <x-page title="Report Absensi">
        <template #header-actions>
            <x-back-button
                :disabled="isLoading"
                :display-warning="false"
                :click-handler="handleBackButtonClick"
            />
        </template>

        <x-filter-box
            :onFilter="handleFilter"
            :onReset="resetFilter"
            :open="true"
            :readonly="isLoading"
        >
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Dari Tanggal">
                        <input
                            id="date_start"
                            type="date"
                            class="form-control"
                            v-model="filters.date_start"
                        />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Sampai Tanggal">
                        <input
                            id="date_end"
                            type="date"
                            class="form-control"
                            v-model="filters.date_end"
                        />
                    </x-form-group>
                </div>
            </div>
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Metrics">
                        <x-input-metrics
                            v-model="filters.ids"
                            class="form-control"
                        />
                    </x-form-group>
                </div>
            </div>
        </x-filter-box>

        <div class="row">
            <!-- Summaries table -->
            <div class="col-xs-12">
                <x-table
                    :headers="['#', 'Metric', 'Total', 'Average', 'Min', 'Max']"
                    :is-loading="isLoading"
                >
                    <tr v-for="(resource, index) in resources" :key="index">
                        <td>{{ rowIndex(index, $route.query) }}</td>
                        <td class="nowrap">
                            {{ resource.name }}
                        </td>
                        <td class="nowrap">
                            {{ resource.sum | number }}
                        </td>
                        <td class="nowrap">
                            {{ resource.average | int }}
                        </td>
                        <td class="nowrap">
                            {{ resource.min | number }}
                        </td>
                        <td class="nowrap">
                            {{ resource.max | number }}
                        </td>
                    </tr>
                </x-table>
            </div>
        </div>

        <x-panel title="Trend">
            <div class="row">
                <!-- Line chart -->
                <div class="col-xs-12 col-md-6">
                    <div class="line-chart-container">
                        <canvas id="line-chart" height="275"></canvas>
                    </div>
                </div>

                <!-- Details table -->
                <div class="col-xs-12 col-md-6">
                    <x-table
                        :is-loading="recordLoading"
                        caption="Records Detail"
                        :headers="['Tanggal', 'Judul']"
                    >
                        <tr
                            v-for="(resource, index) in records"
                            :key="index"
                            v-show="!recordLoading"
                        >
                            <td class="nowrap">
                                <router-link
                                    :to="`/admin/metric-records/${resource.id}`"
                                    title="Klik untuk lihat detail"
                                >
                                    {{ resource.date | date }}
                                </router-link>
                            </td>
                            <td>
                                {{ resource.name }}
                            </td>
                        </tr>
                    </x-table>

                    <x-dumb-pagination
                        v-if="shouldRenderRecordsPagination"
                        :pagination="recordsMeta"
                        @navigate="paginateRecords"
                    />
                </div>
            </div>
        </x-panel>

        <x-panel title="Volume">
            <div class="row">
                <div class="col-xs-12 col-md-4">
                    <select
                        class="form-control"
                        v-model="pieCalculation"
                        style="margin-bottom: 20px"
                    >
                        <option value="sum">Hitung by Total</option>
                        <option value="average">Hitung by Average</option>
                    </select>
                </div>
            </div>
            <div class="row">
                <!-- Pie charts -->
                <div
                    class="col-xs-12 col-md-6 col-lg-4 text-center"
                    v-for="category in categories"
                    :key="category.id"
                    style="margin-bottom: 30px"
                >
                    <b>{{ category.name }}</b>
                    <canvas :id="`pie-chart-${category.id}`"></canvas>
                </div>
            </div>
        </x-panel>

        <x-pagination
            v-if="shouldRenderPagination"
            :pagination="meta"
            :query="$route.query"
        />
    </x-page>
</template>

<script>
import Chart from "chart.js/auto";
import { get, uniqBy } from "lodash";
import XAjaxDropdown from "../../../components/XAjaxDropdown.vue";
import XBackButton from "../../../components/XBackButton.vue";
import XFilterBox from "../../../components/XFilterBox.vue";
import XFormGroup from "../../../components/XFormGroup.vue";
import XInputMetrics from "../../../components/XInputMetrics.vue";
import XPage from "../../../components/XPage.vue";
import XPagination from "../../../components/XPagination.vue";
import XTable from "../../../components/XTable.vue";
import { dateFormat } from "../../../constants";
import {
    formatForDateInput,
    getAvailableMetrics,
    handleGeneralXhrError,
} from "../../../helpers";
import FilterableMixin from "../../../mixins/FilterableMixin";
import PaginationMixin from "../../../mixins/PaginationMixin";
import PermissionMixin from "../../../mixins/PermissionMixin";
import XDumbPagination from "../../../components/XDumbPagination.vue";
import XPanel from "../../../components/XPanel.vue";

const defaultFilters = {
    date_end: moment().clone().endOf("month").format(dateFormat),
    date_start: moment().clone().startOf("month").format(dateFormat),
    ids: null,
};

let lineChart = null;
let pieCharts = [];

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchResources(to);
        this.fetchRecords(to.query.page || 1);
        next();
    },
    components: {
        XTable,
        XPage,
        XFormGroup,
        XFilterBox,
        XAjaxDropdown,
        XPagination,
        XBackButton,
        XInputMetrics,
        XDumbPagination,
        XPanel,
    },
    computed: {
        categories() {
            const categories = getAvailableMetrics()
                .map((metric) => get(metric, "category"))
                .filter((category) => category);
            return uniqBy(categories, "id");
        },
        shouldRenderRecordsPagination() {
            return get(this.recordsMeta, "last_page", 1) > 1;
        },
    },
    created() {
        this.preselectFilters(defaultFilters);
        this.handleFilter().catch(() => {
            this.fetchResources();
            this.fetchRecords();
        });
    },
    data() {
        return {
            filters: { ...defaultFilters },
            isLoading: false,
            pieCalculation: "sum",
            recordLoading: false,
            records: [],
            recordsMeta: null,
            resources: [],
        };
    },
    methods: {
        fetchRecords(page = 1) {
            this.recordLoading = true;
            const request = axios
                .get("/api/metric-records", {
                    params: {
                        date_start: this.filters.date_start,
                        date_end: this.filters.date_end,
                        page: page,
                    },
                })
                .then((response) => {
                    this.records = response.data.data;
                    this.recordsMeta = response.data.meta;
                });
            return handleGeneralXhrError(request).finally(() => {
                this.recordLoading = false;
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get("/api/reports/metrics", {
                    params: {
                        ...route.query,
                        limit: Number.MAX_SAFE_INTEGER,
                        with: ["category"],
                    },
                })
                .then((response) => {
                    this.resources = response.data.data;
                    this.setMeta(response);
                    if (lineChart)
                        updateLineChart(lineChart, response.data.data);
                    this.updatePieCharts();
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        handleBackButtonClick(router) {
            router.replace("/admin/reports");
        },
        handleFilter() {
            return this.$router.push({
                path: this.$route.path,
                query: {
                    ...this.filters,
                    page: 1,
                },
            });
        },
        initLineChart() {
            const ctx = document.getElementById("line-chart");
            const legendMargin = {
                id: "legendMargin",
                beforeInit(chart, legend, options) {
                    const fitValue = chart.legend.fit;
                    chart.legend.fit = function fit() {
                        fitValue.bind(chart.legend)();
                        return (this.height += 10);
                    };
                },
            };
            lineChart = new Chart(ctx, {
                type: "line",
                data: {
                    labels: [],
                    datasets: [],
                },
                plugins: [legendMargin],
            });
            updateLineChart(lineChart, this.resources);
        },
        initPieCharts() {
            const emptyPlugin = {
                id: "emptyChart",
                afterDraw(chart, args, options) {
                    const { datasets } = chart.data;
                    let hasData = false;
                    let data = [];

                    for (let dataset of datasets) {
                        data = dataset.data.filter((item) => item);
                        //set this condition according to your needs
                        if (
                            data.length > 0 &&
                            data.some((item) => item !== 0)
                        ) {
                            hasData = true;
                            break;
                        }
                    }

                    if (!hasData) {
                        //type of ctx is CanvasRenderingContext2D
                        //https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D
                        //modify it to your liking
                        const {
                            chartArea: { left, top, right, bottom },
                            ctx,
                        } = chart;
                        const centerX = (left + right) / 2;
                        const centerY = (top + bottom) / 2;

                        chart.clear();
                        ctx.save();
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillText("No data to display", centerX, centerY);
                        ctx.restore();
                    }
                },
            };
            this.categories.forEach((category, index) => {
                const ctx = document.getElementById(`pie-chart-${category.id}`);
                pieCharts[index] = new Chart(ctx, {
                    type: "doughnut",
                    data: {
                        labels: this.categories.map(
                            (category) => category.name
                        ),
                        datasets: [],
                    },
                    plugins: [emptyPlugin],
                });
                updatePieChart(
                    pieCharts[index],
                    category,
                    this.resources,
                    this.pieCalculation
                );
            });
        },
        paginateRecords(page) {
            this.fetchRecords(page);
        },
        resetFilter() {
            this.filters = { ...defaultFilters };
            this.handleFilter();
        },
        updatePieCharts() {
            this.categories.forEach((category, index) => {
                updatePieChart(
                    pieCharts[index],
                    category,
                    this.resources,
                    this.pieCalculation
                );
            });
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
    mounted() {
        this.initLineChart();
        this.initPieCharts();
    },
    watch: {
        pieCalculation() {
            this.updatePieCharts();
        },
    },
};

function updateLineChart(chart, resources) {
    const labels = resources.reduce((dates, resource) => {
        resource.records.forEach((record) => {
            const date = formatForDateInput(record.date);
            if (!dates.includes(date)) {
                dates.push(date);
            }
        });
        return dates;
    }, []);
    const datasets = resources.reduce((sets, resource) => {
        const name = resource.name;
        const set = sets.find((item) => item.label == name);
        let color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;

        const oldSet = chart.data.datasets.find((item) => item.label == name);
        if (oldSet) {
            color = oldSet.backgroundColor;
        }

        if (!set) {
            sets.push({
                label: name,
                data: labels.map((label) => {
                    const record = resource.records.find(
                        (record) => formatForDateInput(record.date) == label
                    );
                    const value = get(record, "pivot.value");
                    return value || 0;
                }),
                fill: false,
                borderColor: color,
                backgroundColor: color,
                tension: 0.1,
            });
        }
        return sets;
    }, []);

    chart.data.labels = [...labels];
    chart.data.datasets = [...datasets];
    chart.update();
}

function updatePieChart(chart, category, resources, pieCalculation) {
    const filteredResources = resources.filter(
        (resource) => resource.category_id == category.id
    );
    let colors = filteredResources.map((resource, index) => {
        const oldColor = get(chart, `data.datasets.backgroundColor.${index}`);
        return (
            oldColor || `#${Math.floor(Math.random() * 16777215).toString(16)}`
        );
    });

    chart.data.labels = filteredResources.map((resource) => resource.name);
    chart.data.datasets = [
        {
            label: category.name,
            data: filteredResources.map((resource) =>
                parseInt(get(resource, pieCalculation, 0))
            ),
            backgroundColor: colors,
            hoverOffset: 4,
        },
    ];
    chart.update();
}
</script>
